import type { ComponentProps, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Marker } from 'react-map-gl/mapbox'
import { Box } from '../../components/Box.tsx'
import { Divider, Tooltip, Typography } from '../../components/generic.tsx'
import { IconButton } from '../../components/IconButton.tsx'
import { ids } from '../../ids.ts'
import { useMapTheme } from '../../theme/mapPalette.ts'

export const MapCoordinateMarker = ({
	latitude,
	longitude,
	closable = false,
	onClose,
	children,
	...props
}: {
	latitude: number
	longitude: number
	closable?: boolean
	onClose?: (e: React.MouseEvent<HTMLButtonElement>) => void
	children?: ReactNode
} & ComponentProps<typeof Marker>) => {
	const { t } = useTranslation()
	const mapTheme = useMapTheme()
	return (
		<Marker {...props} latitude={latitude} longitude={longitude}>
			<Tooltip
				open
				slotProps={{
					popper: { disablePortal: true, sx: { px: 1, pointerEvents: 'none' } },
					tooltip: {
						sx: {
							backgroundColor: mapTheme.mapCoordinate.bg,
							color: mapTheme.mapCoordinate.fg,
						},
					},
					arrow: {
						sx: {
							color: mapTheme.mapCoordinate.bg,
						},
					},
				}}
				placement='top'
				arrow
				title={
					<Box data-ta={ids.map.mapMarker.coordinateMarker.root} flex center gap={1}>
						{children || (
							<Box sx={{ display: 'grid', gridTemplateColumns: '80px 1px 80px', gridTemplateRows: '1fr', gap: 1, alignItems: 'center' }}>
								<Typography data-ta={ids.map.mapMarker.coordinateMarker.latitude} variant='body1' fontSize='small' textAlign='right'>
									{latitude.toFixed(5)}
								</Typography>
								<Divider orientation='vertical' flexItem sx={{ borderColor: mapTheme.mapCoordinate.fg }} />
								<Typography data-ta={ids.map.mapMarker.coordinateMarker.longitude} variant='body1' fontSize='small' textAlign='left'>
									{longitude.toFixed(5)}
								</Typography>
							</Box>
						)}
						{closable && (
							<IconButton
								icon='Close'
								label={t(($) => $.mapTools.distanceTool.clearDistance)}
								size='xsmall'
								color='secondary'
								sx={{
									color: mapTheme.mapCoordinate.fg,
									':hover': { backgroundColor: mapTheme.mapCoordinate.hover },
									pointerEvents: 'auto',
								}}
								onClick={onClose}
							/>
						)}
					</Box>
				}
			>
				<Box />
			</Tooltip>
		</Marker>
	)
}
